"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    Shield,
    FileCheck,
    UserCheck,
    CheckCircle2,
    ArrowRight,
    BadgeCheck,
    Lock,
    FileSearch,
    LucideIcon
} from "lucide-react"
import Link from "next/link"

const services: {
    id: string
    icon: LucideIcon
    title: string
    description: string
    features: string[]
    badge: string
    ctaText: string
    ctaLink: string
}[] = [
        {
            id: "kyc",
            icon: UserCheck,
            title: "KYC Verification",
            description: "Build trust with your investors. Verify the identity of your project founders and team members.",
            features: [
                "Identity document verification",
                "Live selfie verification",
                "Address verification",
                "Background checks",
                "KYC badge on your presale"
            ],
            badge: "Build Trust",
            ctaText: "Apply for KYC",
            ctaLink: "/kyc"
        },
        {
            id: "audit",
            icon: FileSearch,
            title: "Smart Contract Audit",
            description: "Make sure your smart contracts are safe and optimized. Our team reviews your code for bugs and risks.",
            features: [
                "Vulnerability assessment",
                "Code optimization review",
                "Gas efficiency analysis",
                "Detailed audit report",
                "Audit badge on your project"
            ],
            badge: "Security First",
            ctaText: "Request Audit",
            ctaLink: "/audit"
        }
    ]

const stats = [
    { value: "Free", label: "Basic Audit" },
    { value: "Built-in", label: "KYC & Verification" },
    { value: "0", label: "Security Breaches" },
    { value: "24-48h", label: "Turnaround Time" },
]

export function KycAuditSection() {
    return (
        <section className="py-20 md:py-28 bg-muted/30" id="kyc-audit">
            <div className="container">
                {/* Header */}
                <div className="mb-16 text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                        <Shield className="h-4 w-4" />
                        Trust & Security Services
                    </div>
                    <h2 className="mb-5 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                        KYC &{" "}
                        <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                            Audit
                        </span>{" "}
                        Services
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                        Build credibility and keep your investors safe with our
                        KYC and smart contract audit services.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid gap-8 lg:grid-cols-2 mb-16">
                    {services.map((service) => {
                        const Icon = service.icon
                        return (
                            <Card
                                key={service.id}
                                className="group relative overflow-hidden border-border/40 dark:border-transparent hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10"
                            >
                                {/* Gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                <CardContent className="relative p-8">
                                    {/* Badge */}
                                    <div className="absolute top-6 right-6">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                                            <BadgeCheck className="h-3.5 w-3.5" />
                                            {service.badge}
                                        </span>
                                    </div>

                                    {/* Icon */}
                                    <div className="mb-6">
                                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 group-hover:from-primary/30 group-hover:to-primary/10 transition-all duration-300 group-hover:scale-110">
                                            <Icon className="h-8 w-8 text-primary" />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <h3 className="mb-3 text-2xl font-bold">{service.title}</h3>
                                    <p className="mb-6 text-muted-foreground leading-relaxed">
                                        {service.description}
                                    </p>

                                    {/* Features */}
                                    <ul className="mb-8 space-y-3">
                                        {service.features.map((feature, index) => (
                                            <li key={index} className="flex items-center gap-3 text-sm">
                                                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <Button asChild className="w-full group/btn">
                                        <Link href={service.ctaLink}>
                                            {service.ctaText}
                                            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                                        </Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>

                {/* Stats Bar */}
                <div className="glass-card p-8">
                    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                                    {stat.value}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-12 text-center">
                    <p className="text-sm text-muted-foreground mb-6">
                        Verified projects receive trust badges displayed on their presale pages
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 p-4 rounded-2xl bg-muted/50 max-w-xl mx-auto">
                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#7ed957]/10 border border-[#7ed957]/20">
                            <UserCheck className="h-5 w-5 text-[#7ed957]" />
                            <span className="font-semibold text-[#7ed957]">KYC Verified</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                            <FileCheck className="h-5 w-5 text-blue-500" />
                            <span className="font-semibold text-blue-500">Audit Passed</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 border border-primary/20">
                            <Lock className="h-5 w-5 text-primary" />
                            <span className="font-semibold text-primary">LP Locked</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
